/*
 * Backbone View: LoggingView
 * ----------------------
 * This is the template for an log view. It creates a div that displays the
 * log information associated with the current device/agent.
 *
 * This is the logging view used in the code editor & device details
 *
 *
 */
import _ from 'underscore';
import Marionette from 'backbone.marionette';
import Handlebars from 'handlebars';
import PaneParentView from '@/views/pane_parent_view';
import template from '@/../templates/ide-log-template.hbs';
import no_devices_template from '@/../templates/ide-log-no-devices.hbs';
import strings_common from 'amdi18n-loader!@/nls/code_editor';
import logstream from '@/collections/v5/logstream';
import date_m from '@/lib/date_milli_helper';

    var strings = _.extend({}, strings_common);


    // HTML Escape helper utility
    var util = (function () {
      // Thanks to Andrea Giammarchi
      // from https://developers.google.com/web/updates/2015/01/ES6-Template-Strings
      var
        reEscape = /[&<>'"]/g,
        reUnescape = /&(?:amp|#38|lt|#60|gt|#62|apos|#39|quot|#34);/g,
        oEscape = {
          '&': '&amp;',
          '<': '&lt;',
          '>': '&gt;',
          "'": '&#39;',
          '"': '&quot;'
        },
        oUnescape = {
          '&amp;': '&',
          '&#38;': '&',
          '&lt;': '<',
          '&#60;': '<',
          '&gt;': '>',
          '&#62;': '>',
          '&apos;': "'",
          '&#39;': "'",
          '&quot;': '"',
          '&#34;': '"'
        },
        fnEscape = function (m) {
          return oEscape[m];
        },
        fnUnescape = function (m) {
          return oUnescape[m];
        },
        replace = String.prototype.replace
      ;
      return (Object.freeze || Object)({
        escape: function escape(s) {
          return replace.call(s, reEscape, fnEscape);
        },
        unescape: function unescape(s) {
          return replace.call(s, reUnescape, fnUnescape);
        }
      });
    }());
    
    // Tagged template function
    function html(pieces) {
        var result = pieces[0];
        var substitutions = [].slice.call(arguments, 1);
        for (var i = 0; i < substitutions.length; ++i) {
            result += util.escape(substitutions[i]) + pieces[i + 1];
        }
    
        return result;
    }

    function get_type_info(type) {
        var type_info = {
            name: type,
            class_name: ''
        };

        switch (type) {
        case 'server.log':
            type_info.name = 'Device';
            type_info.class_name = 'device-log';
            break;
        case 'server.error':
            type_info.name= 'Device';
            type_info.class_name = 'device-error';
            break;
        case 'server.sleep':
            type_info.name= 'Device';
            type_info.class_name = 'device-sleep';
            break;
        case 'agent.log':
            type_info.name= 'Agent';
            type_info.class_name = 'agent-log';
            break;
        case 'agent.error':
            type_info.name= 'Agent';
            type_info.class_name = 'agent-error';
            break;
        case 'status':
            type_info.name= 'Status';
            type_info.class_name = 'status';
            break;
        case 'powerstate':
            type_info.name= 'Power State';
            type_info.class_name = 'power-state';
            break;
        case 'lastexitcode':
            type_info.name= 'Exit Code';
            type_info.class_name = 'last-exit-code';
            break;
        case 'firmware':
            type_info.name= 'Firmware';
            type_info.class_name = 'firmware';
            break;
        case 'IDE.log':
            type_info.name= 'IDE';
            type_info.class_name = 'ide-log';
            break;
        }

        return type_info;
    }

    var LoggingView = Marionette.View.extend({
        className : 'logging-pane',
        template: Handlebars.compile(template),
        childViewContainer: 'table',
        reorderOnSort: true,
        ui: {
            log_container: 'table'
        },
        templateContext: function(){
            return strings;
        },
        triggers: {
            'mouseenter': 'lock',
            'mouseleave': 'unlock'
        },
        /*
         * Function: initialize
         * --------------------
         * Initializes the view. Sets the deviceID for this object and calls
         * the parent init function.
         */
        initialize: function(options) {
            this.once('LogLimitMessage', this.logLimitMessage);
            this.listenToOnce(this.model, 'sync', this.scrollToBottom);
            PaneParentView.prototype.initialize.call(this, options);
        },

        logLimitMessage : function(show){
          var self = this;
          this.$el.find('.log-status-message')
              .addClass('alert-success active')
              .text(strings.log_limit);
          setTimeout(function(){
              self.$el.find('.log-status-message')
                    .fadeOut(200)
                    .removeClass('alert-success active')
                    .text('');
          }, 3000);
        },
        onRender: function() {
            var self = this;
            this.set_autoscroll(true);

            this.model.logs.fetch_logs().then(
                function(){
                    self.read_logs(self.model.logs.get('logs'));
                },
                function(e, d) { console.warn('Error Fetching Logs', e, d); }
            ).then( function(){
                self.start_live_logs(self.model);
            });
        },
        read_logs: function(logs) {
            for (let i = 0; i < logs.length; i++) {
                this.add_log(logs[i]);
            }
        },
        onDestroy: function() {
            // Incredibly, Backbone/Marionette doesn't have destroyed views stop listening for events. 
            // Need to do this manually to prevent logs from being added to the collection from destroyed views.
            this.stopListening();
        },
        renderEmpty: function(){
            this.$el.html(no_devices_template);
        },
        event_name: function(device){
            return 'device_id:' + device.id;
        },
        start_live_logs: function(device){
            var self = this;
            var eventname = this.event_name(device);
            this.listenTo(logstream, eventname, function (log) {
                // For some reason, this isn't causing log items to have an ID. Not being picked up by the log item parser,
                // Had to add ID manually for now.
                if (log.type) {
                    log.id = log.ts + log.msg;
                    self.add_log(log);
                    self.model.logs.get('logs').push(log);
                    self.model.logs.trigger('change:logs');
                }
            });
            this.listenTo(logstream, 'state', function (state) {
                if (state === 'closed') {
                    var date = new Date().toISOString();
                    self.add_log({
                        msg: 'Realtime logging has stopped. Please refresh to enable it again.',
                        ts: date,
                        type: 'IDE.log',
                    });
                }
            });
            logstream.add(device);
            this.scrollToBottom();
        },
        add_log: function(log){
            let type_info = get_type_info(log.type);
            log.type_name = type_info.name;
            log.type_class = type_info.class_name;
            let message = html`${log.msg}`;

            let log_template = `<tr class="entry">
                    <td class="timestamp">${date_m(log.ts)}</td>
                    <td><span class="type ${log.type_class}">[${log.type_name}]</span></td>
                    <td class="message">${message}</td>
                </tr>`;

            this.ui.log_container.append(log_template);

            while (_.isFunction(this.ui.log_container.find) && this.ui.log_container.find('tr').length > 1000) {
                this.ui.log_container.find('tr').first().remove();
            }
        },
        /**
         * Scroll to the bottom of the log, to emulate tail-like output.
         */
        scrollToBottom : function() {
            var $logContainer = this.$el.find('#log');

            if (this.$el.find('#log:hover').length === 0) {
                $logContainer.scrollTop($logContainer[0].scrollHeight);
            }
        },
        addSyntaxErrorLog: function(file, lineNum, errorMsg, type) {
            var message =
                (type === 'warning' ? 'Compiler warning' : 'Syntax error')
                +  ' (line ' + lineNum + '): ' + errorMsg;

            var errorType = 'IDE.log';
            if (file === 'device_code') {
                errorType = 'server.error';
            } else if (file === 'agent_code') {
                errorType = 'agent.error';
            }
            var ts = new Date().toISOString();
            this.add_log({
                ts: ts,
                type: errorType,
                msg: message
            });
        },
        clear_log: function() {
            this.model.logs.set('logs', []);
            this.ui.log_container.html('');
        },
        enable_log: function(device) {
            var self = this;
            var eventname = this.event_name(device);
            this.stopListening(logstream, eventname);
            this.listenTo(logstream, eventname, function (log) {
                // For some reason, this isn't causing log items to have an ID. Not being picked up by the log item parser,
                // Had to add ID manually for now.
                if (log.type) {
                    log.id = log.ts + log.msg;
                    self.add_log(log);
                    self.model.logs.get('logs').push(log);
                    self.model.logs.trigger('change:logs');
                }
            });
            var ts = new Date().toISOString();
            this.add_log({
                msg: strings.logging_unmuted,
                ts: ts,
                type: "IDE.log"
            });
            this.set_autoscroll(true);
        },
        get_last_log: function() {
            // To get the last log message, it could either be either
            // IDE generated (ex: anywhere in this file that says 'add_log({...})')
            // or
            // an actual log from the API
            // At any rate, return the contents of the last tr row, interpret
            // and return
            var last_tr = this.ui.log_container.find('tr:last').html();
            return last_tr;
        },
        disable_log: function(device) {
            var eventname = this.event_name(device);
            this.stopListening(logstream, eventname);
            
            var ts = new Date().toISOString();
            this.add_log({
                msg: strings.logging_muted,
                ts: ts,
                type: "IDE.log"
            });
            
            this.scrollToBottom();
        },
        set_autoscroll: function(enabled) {
            this.stopListening(this.model.logs, 'change:logs');

            if (enabled) {
                this.listenTo(this.model.logs, 'change:logs', this.scrollToBottom);
            }
        },
        onLock: function() {
            this.set_autoscroll(false);
        },
        onUnlock: function() {
            this.set_autoscroll(true);
        },
    });


    export default LoggingView;

